import React, { useState, useEffect } from 'react';
import { categoryAPI } from '../../services/api.js';
import toast from 'react-hot-toast';

const CategoryForm = ({ category, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent: '',
    status: 'active',
    seo: {
      title: '',
      description: '',
      keywords: '',
      slug: '',
    },
    icon: '📁',
    order: 0,
    level: 1,
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch categories for parent dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryAPI.getAll?.() || await categoryAPI.getHierarchy?.();
        if (response) {
          const categoryList = response.data || response.categories || response.result || response;
          if (Array.isArray(categoryList)) {
            setCategories(categoryList);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Populate form if editing
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        parent: category.parent?._id || category.parent || '',
        status: category.status || 'active',
        seo: {
          title: category.seo?.title || category.name || '',
          description: category.seo?.description || category.description || '',
          keywords: category.seo?.keywords || '',
          slug: category.seo?.slug || category.slug || '',
        },
        icon: category.icon || '📁',
        order: category.order || 0,
        level: category.level || 1,
      });
    }
  }, [category]);

  // Check what methods are available on categoryAPI
  useEffect(() => {
    console.log('categoryAPI methods:', Object.keys(categoryAPI || {}));
    console.log('categoryAPI object:', categoryAPI);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('seo.')) {
      const seoField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        seo: {
          ...prev.seo,
          [seoField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      setLoading(false);
      return;
    }

    // Prepare clean data for API
    const submitData = {
      name: formData.name.trim(),
      description: formData.description?.trim() || '',
      status: formData.status || 'active',
      icon: formData.icon || '📁',
      order: parseInt(formData.order) || 0,
      featured: formData.featured || false
    };

    // Only add parent if it's not empty
    if (formData.parent && formData.parent.trim()) {
      submitData.parent = formData.parent;
    }

    // Only add SEO data if we have any fields
    const hasSeoData = formData.seo.title || formData.seo.description || 
                      formData.seo.keywords || formData.seo.slug;
    
    if (hasSeoData) {
      submitData.seo = {
        title: formData.seo.title?.trim() || '',
        description: formData.seo.description?.trim() || '',
        keywords: formData.seo.keywords?.trim() || '',
        slug: formData.seo.slug?.trim() || ''
      };
    }

    // Only add level if it's specified (backend will calculate it)
    if (formData.level) {
      submitData.level = parseInt(formData.level);
    }

    console.log('📤 Submitting category data:', submitData);
    console.log('🔧 Available API methods:', Object.keys(categoryAPI || {}));

    let response;
    
    if (category) {
      // Editing existing category
      console.log('🔄 Updating category:', category._id);
      response = await categoryAPI.update(category._id, submitData);
      toast.success('Category updated successfully!');
    } else {
      // Creating new category
      console.log('🆕 Creating new category');
      response = await categoryAPI.create(submitData);
      toast.success('Category created successfully!');
    }

    console.log('✅ API Response:', response);
    
    if (response && response.success) {
      onSave();
    } else {
      throw new Error(response?.message || 'Unknown error');
    }
    
  } catch (error) {
    console.error('❌ Error saving category:', error);
    console.error('🔍 Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    
    // Show specific error message from backend if available
    if (error.response?.data?.message) {
      toast.error(`Failed to ${category ? 'update' : 'create'} category: ${error.response.data.message}`);
      
      // Show validation errors if available
      if (error.response.data.errors) {
        error.response.data.errors.forEach(err => {
          toast.error(`Validation: ${err}`, { duration: 5000 });
        });
      }
    } else {
      toast.error(`Failed to ${category ? 'update' : 'create'} category: ${error.message}`);
    }
  } finally {
    setLoading(false);
  }
};

  const generateSlug = () => {
    const slug = formData.name
      .toLowerCase()
      .replace(/[^\w\s]/gi, '')
      .replace(/\s+/g, '-');
    
    setFormData(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        slug: slug
      }
    }));
  };

  // Responsive styles
  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: isMobile ? 'var(--space-2)' : 'var(--space-4)'
  };

  const contentStyle = {
    width: '100%',
    maxWidth: isMobile ? '95vw' : '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    borderRadius: 'var(--radius-lg)'
  };

  const formGroupStyle = {
    marginBottom: isMobile ? 'var(--space-3)' : 'var(--space-4)'
  };

  const inputStyle = {
    padding: isMobile ? '8px 12px' : '10px 16px',
    fontSize: isMobile ? '14px' : 'var(--font-size-base)',
    width: '100%'
  };

  const labelStyle = {
    fontSize: isMobile ? '14px' : 'var(--font-size-base)',
    marginBottom: '4px',
    display: 'block',
    fontWeight: '500'
  };

  return (
    <div style={modalStyle}>
      <div className="card" style={contentStyle}>
        <div className="card-header" style={{
          padding: isMobile ? 'var(--space-3)' : 'var(--space-4)',
          position: 'sticky',
          top: 0,
          backgroundColor: 'var(--bg-white)',
          zIndex: 10,
          borderBottom: '1px solid var(--border-light)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isMobile ? '18px' : 'var(--font-size-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flex: 1
            }}>
              <span style={{ color: 'var(--accent-gold)' }}>
                {category ? '✏️' : '➕'}
              </span>
              {category ? 'Edit Category' : 'Create New Category'}
            </h3>
            
            <button 
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: 'var(--text-light)',
                padding: '4px',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card-body" style={{
            padding: isMobile ? 'var(--space-3)' : 'var(--space-4)'
          }}>
            {/* Basic Information */}
            <div style={formGroupStyle}>
              <h4 style={{ 
                marginBottom: isMobile ? 'var(--space-2)' : 'var(--space-3)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: 'var(--space-1)',
                fontSize: isMobile ? '16px' : 'var(--font-size-base)'
              }}>
                Basic Information
              </h4>
              
              <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                <div>
                  <label style={labelStyle}>
                    Category Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter category name"
                    required
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Enter category description"
                    rows={3}
                    style={inputStyle}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 'var(--space-3)' }}>
                  <div>
                    <label style={labelStyle}>
                      Parent Category
                    </label>
                    <select
                      name="parent"
                      value={formData.parent}
                      onChange={handleInputChange}
                      className="form-select"
                      style={inputStyle}
                    >
                      <option value="">No Parent (Root Category)</option>
                      {categories.map((cat) => (
                        <option key={cat._id || cat.id} value={cat._id || cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="form-select"
                      style={inputStyle}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 'var(--space-3)' }}>
                  <div>
                    <label style={labelStyle}>
                      Icon
                    </label>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      <select
                        name="icon"
                        value={formData.icon}
                        onChange={handleInputChange}
                        className="form-select"
                        style={{ ...inputStyle, flex: 1 }}
                      >
                        <option value="📁">📁 Folder</option>
                        <option value="📂">📂 Open Folder</option>
                        <option value="📄">📄 Document</option>
                        <option value="📊">📊 Chart</option>
                        <option value="📈">📈 Graph</option>
                        <option value="🛒">🛒 Cart</option>
                        <option value="👕">👕 Clothing</option>
                        <option value="📱">📱 Electronics</option>
                        <option value="🏠">🏠 Home</option>
                        <option value="🎮">🎮 Games</option>
                      </select>
                      <span style={{ fontSize: '20px' }}>{formData.icon}</span>
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      className="form-input"
                      min="0"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SEO Information */}
            <div style={formGroupStyle}>
              <h4 style={{ 
                marginBottom: isMobile ? 'var(--space-2)' : 'var(--space-3)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: 'var(--space-1)',
                fontSize: isMobile ? '16px' : 'var(--font-size-base)'
              }}>
                SEO Settings
              </h4>
              
              <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                <div>
                  <label style={labelStyle}>
                    SEO Title
                  </label>
                  <input
                    type="text"
                    name="seo.title"
                    value={formData.seo.title}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter SEO title"
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>
                    SEO Description
                  </label>
                  <textarea
                    name="seo.description"
                    value={formData.seo.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Enter SEO description"
                    rows={2}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>
                    Keywords
                  </label>
                  <input
                    type="text"
                    name="seo.keywords"
                    value={formData.seo.keywords}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="comma, separated, keywords"
                    style={inputStyle}
                  />
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={labelStyle}>
                      URL Slug
                    </label>
                    <button
                      type="button"
                      onClick={generateSlug}
                      style={{
                        background: 'var(--bg-gray)',
                        border: '1px solid var(--border-medium)',
                        padding: '4px 8px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Generate from Name
                    </button>
                  </div>
                  <input
                    type="text"
                    name="seo.slug"
                    value={formData.seo.slug}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="url-friendly-slug"
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="card-footer" style={{
            padding: isMobile ? 'var(--space-3)' : 'var(--space-4)',
            borderTop: '1px solid var(--border-light)',
            backgroundColor: 'var(--bg-light)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-2)'
          }}>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              style={{
                padding: isMobile ? '8px 16px' : 'var(--space-3) var(--space-4)',
                fontSize: isMobile ? '14px' : 'var(--font-size-base)'
              }}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={loading}
              className="btn btn-gold"
              style={{
                padding: isMobile ? '8px 16px' : 'var(--space-3) var(--space-4)',
                fontSize: isMobile ? '14px' : 'var(--font-size-base)',
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? (
                <>
                  <span style={{ 
                    display: 'inline-block', 
                    animation: 'spin 1s linear infinite',
                    marginRight: '8px'
                  }}>
                    ⏳
                  </span>
                  {category ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <span style={{ marginRight: '8px' }}>
                    {category ? '💾' : '✨'}
                  </span>
                  {category ? 'Update Category' : 'Create Category'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .card {
          background-color: var(--bg-white);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-xl);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .form-input, .form-select, .form-textarea {
          width: 100%;
          border: 1px solid var(--border-medium);
          border-radius: var(--radius-md);
          background-color: var(--bg-white);
          color: var(--text-dark);
          transition: all 0.2s ease;
          font-family: inherit;
        }
        
        .form-input:focus, .form-select:focus, .form-textarea:focus {
          outline: none;
          border-color: var(--accent-gold);
          box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1);
        }
        
        .btn {
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
        }
        
        .btn-gold {
          background-color: var(--accent-gold);
          color: white;
        }
        
        .btn-gold:hover:not(:disabled) {
          background-color: var(--accent-gold-dark);
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }
        
        .btn-outline {
          background-color: transparent;
          color: var(--text-dark);
          border: 1px solid var(--border-medium);
        }
        
        .btn-outline:hover {
          background-color: var(--bg-light);
          border-color: var(--border-dark);
        }
        
        @media (max-width: 768px) {
          .form-input, .form-select, .form-textarea {
            font-size: 16px; /* Prevent zoom on iOS */
          }
        }
      `}</style>
    </div>
  );
};

export default CategoryForm;